import { useEffect, useState } from 'react'

/** MCP OAuth consent 페이지. backend `/oauth2/authorize` 가 인증된 사용자 + 클라이언트 정보를
 *  쿼리스트링으로 redirect 해 옴. 사용자가 Approve/Deny → POST /oauth2/authorize → backend
 *  가 redirect_uri 응답 (또는 401 면 재인증). */
export function OAuthConsent() {
  const params = new URLSearchParams(window.location.search)
  const clientId = params.get('client_id') || ''
  const clientName = params.get('client_name') || 'MCP Client'
  const scope = params.get('scope') || 'mcp:read mcp:tools'
  const state = params.get('state') || ''
  const redirectUri = params.get('redirect_uri') || ''
  const codeChallenge = params.get('code_challenge') || ''
  const codeChallengeMethod = params.get('code_challenge_method') || 'S256'
  const userEmail = params.get('user_email') || ''

  const [busy, setBusy] = useState(false)
  const [subtitle, setSubtitle] = useState('Review the permissions requested')
  const [subtitleColor, setSubtitleColor] = useState('var(--text-muted)')

  useEffect(() => {
    document.title = 'Authorize Application — AI-README'
  }, [])

  const submit = async (approved: boolean) => {
    setBusy(true)
    const body = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      scope,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: codeChallengeMethod,
      approved: String(approved),
    })
    try {
      const r = await fetch('/oauth2/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body: body.toString(),
      })
      if (r.status === 401) {
        window.location.href = '/oauth/loading?' + window.location.search.substring(1)
        return
      }
      const data = await r.json()
      if (data && data.redirect_uri) {
        if (approved) {
          setSubtitle('Connected. Redirecting…')
          setSubtitleColor('var(--success)')
        }
        setTimeout(() => { window.location.href = data.redirect_uri }, 500)
      } else {
        throw new Error(data?.error_description || data?.error || 'no redirect_uri')
      }
    } catch (e) {
      console.error('consent submit error', e)
      alert('Authorization failed: ' + (e instanceof Error ? e.message : String(e)))
      setBusy(false)
    }
  }

  return (
    <main style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
      <div className="consent-card">
        <h1 style={{ textAlign: 'center', fontSize: 22, marginBottom: 6 }}>Authorize Application</h1>
        <p style={{ textAlign: 'center', color: subtitleColor, fontSize: 13, marginBottom: 24 }}>{subtitle}</p>

        <div className="client-box" style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--accent)', marginBottom: 4 }}>{clientName}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Signed in as <span>{userEmail || '(unknown)'}</span></div>
        </div>

        <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 10, fontSize: 13 }}>This application would like to:</div>
        <PermItem
          icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
          title="Read documents"
          desc="List groups, projects, folders, and read document contents"
        />
        <PermItem
          icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>}
          title="Manage documents"
          desc="Create, update, move, and delete documents, folders, projects, and groups"
        />

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => submit(false)} style={{ flex: 1, padding: '11px 16px', fontSize: 14, justifyContent: 'center' }}>Deny</button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => submit(true)} style={{ flex: 1, padding: '11px 16px', fontSize: 14, justifyContent: 'center' }}>{busy ? 'Processing…' : 'Approve'}</button>
        </div>

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border-subtle)', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', wordBreak: 'break-all' }}>
          Redirects to <code style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-code)', padding: '1px 6px', borderRadius: 'var(--r-sm)' }}>{redirectUri}</code>
        </div>
      </div>
    </main>
  )
}

function PermItem({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '10px 12px', background: 'var(--bg-sidebar)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', marginBottom: 6 }}>
      <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div>
        <strong style={{ color: 'var(--text)', display: 'block', fontSize: 13, marginBottom: 2 }}>{title}</strong>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{desc}</span>
      </div>
    </div>
  )
}
